import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Zap, Brain, Search, MessageSquare, TrendingUp, ArrowRight,
  CheckCircle, XCircle, Star, ChevronDown, Globe, BarChart2,
  Cpu, Shield, Award, Users, Clock, Target, Lightbulb
} from 'lucide-react';
import CountUp from 'react-countup';
import { useInView as useInViewObserver } from 'react-intersection-observer';
import LiveDate from '../components/LiveDate';
import './Home.css';

const SERVICES = [
  { name: 'GEO', full: 'Generative Engine Optimization', icon: '⚡', path: '/services/geo', color: '#00d4ff', desc: 'Rank in AI-generated answers from ChatGPT, Gemini & Perplexity.' },
  { name: 'AEO', full: 'Answer Engine Optimization', icon: '🎯', path: '/services/aeo', color: '#8b5cf6', desc: 'Appear in featured snippets & direct AI answers across all platforms.' },
  { name: 'AIO', full: 'AI Optimization', icon: '🤖', path: '/services/aio', color: '#ec4899', desc: 'Full-spectrum AI presence optimization across all intelligent engines.' },
  { name: 'CAO', full: 'Conversational AI Optimization', icon: '💬', path: '/services/cao', color: '#f59e0b', desc: 'Capture voice search & conversational AI queries on every device.' },
  { name: 'LLMO', full: 'LLM Optimization', icon: '🧠', path: '/services/llmo', color: '#10b981', desc: 'Get cited by large language models as the authoritative source.' },
  { name: 'GAIO', full: 'Generative AI Optimization', icon: '✨', path: '/services/gaio', color: '#00d4ff', desc: 'Optimize content for AI text generation outputs and citations.' },
  { name: 'AISO', full: 'AI Search Optimization', icon: '🔍', path: '/services/aiso', color: '#8b5cf6', desc: 'Dominate new AI-powered search engines replacing traditional Google.' },
  { name: 'SRO', full: 'Search Response Optimization', icon: '📡', path: '/services/sro', color: '#ec4899', desc: 'Craft content that AI uses verbatim in search response answers.' },
  { name: 'QEO', full: 'Query Engine Optimization', icon: '❓', path: '/services/qeo', color: '#f59e0b', desc: 'Match user query intent with AI-optimized structured content.' },
  { name: 'GSO', full: 'Generative Search Optimization', icon: '🌐', path: '/services/gso', color: '#10b981', desc: 'Appear in Google SGE & Bing AI Copilot generated results.' },
];

const STATS = [
  { value: 150, suffix: '+', label: 'Businesses Optimized', icon: <Users size={20} /> },
  { value: 12, suffix: '+', label: 'Years Experience', icon: <Clock size={20} /> },
  { value: 98, suffix: '%', label: 'Client Satisfaction', icon: <Star size={20} /> },
  { value: 40, suffix: 'x', label: 'AI Visibility Boost', icon: <TrendingUp size={20} /> },
];

const OLD_VS_NEW = [
  { old: 'Blue link clicks on Google', new: 'AI-generated answer citations' },
  { old: 'Keyword stuffing & density', new: 'Entity-based semantic content' },
  { old: 'Backlinks as authority signal', new: 'E-E-A-T & author expertise signals' },
  { old: 'Page rank & domain authority', new: 'LLM training data inclusion' },
  { old: 'Meta keywords optimization', new: 'Structured data & schema markup' },
  { old: 'Google Search Console only', new: 'Multi-platform AI monitoring' },
  { old: 'One search engine focus', new: 'ChatGPT + Gemini + Perplexity + Claude' },
  { old: 'Text-based content only', new: 'Multi-modal AI-ready content' },
];

const FAQS = [
  {
    q: 'What is AEO (Answer Engine Optimization)?',
    a: 'AEO is the process of optimizing your website content so that AI-powered answer engines like ChatGPT, Google Gemini, Perplexity AI, and Microsoft Copilot recommend your business as a trusted source when users ask relevant questions.'
  },
  {
    q: 'How is AEO different from traditional SEO?',
    a: 'Traditional SEO focuses on ranking in Google blue links. AEO/GEO focuses on getting your brand cited inside AI-generated answers. In 2025, over 40% of searches result in zero clicks — because AI answers the question directly. AEO captures this new traffic.'
  },
  {
    q: 'Which AI platforms do you optimize for?',
    a: 'We optimize for ChatGPT, Google Gemini, Perplexity AI, Microsoft Copilot, Claude (Anthropic), Grok (xAI), You.com, and Google SGE (Search Generative Experience). As new platforms emerge, we continuously adapt your strategy.'
  },
  {
    q: 'Do you provide services across Kerala?',
    a: 'Yes! We serve clients across all 14 districts of Kerala — Thiruvananthapuram, Kochi, Kozhikode, Thrissur, Kannur, Kollam, Alappuzha, Malappuram, Palakkad, Kottayam, Idukki, Ernakulam, Wayanad, and Kasaragod.'
  },
  {
    q: 'How long does it take to see results from AEO?',
    a: 'Unlike traditional SEO which takes 3-6 months, AEO improvements can be seen in 4-8 weeks as AI models update their knowledge. Structured data and schema implementation shows the fastest results within days.'
  },
  {
    q: 'What is GEO (Generative Engine Optimization)?',
    a: 'GEO is the optimization of your content specifically for generative AI systems like ChatGPT and Google Gemini. When someone asks an AI "best [service] in Kerala," GEO ensures your business appears in that generated response.'
  },
];

const AnimatedCounter = ({ value, suffix }) => {
  const { ref, inView } = useInViewObserver({ triggerOnce: true, threshold: 0.5 });
  return (
    <span ref={ref} className="stat-number">
      {inView ? <CountUp end={value} duration={2} suffix={suffix} /> : `0${suffix}`}
    </span>
  );
};

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className={`faq-item ${open ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      viewport={{ once: true }}
    >
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <ChevronDown size={18} className={`faq-icon ${open ? 'rotated' : ''}`} />
      </button>
      {open && (
        <motion.div
          className="faq-answer"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <p>{a}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

const Home = () => {
  const [typedText, setTypedText] = useState('');
  const phrases = ['ChatGPT Results', 'Google Gemini', 'Perplexity AI', 'AI Answers', 'Microsoft Copilot'];
  const [phraseIdx, setPhraseIdx] = useState(0);

  useEffect(() => {
    let i = 0;
    const current = phrases[phraseIdx];
    let typing = true;
    let timer;

    const type = () => {
      if (typing) {
        if (i <= current.length) {
          setTypedText(current.slice(0, i));
          i++;
          timer = setTimeout(type, 80);
        } else {
          typing = false;
          timer = setTimeout(type, 2000);
        }
      } else {
        if (i > 0) {
          i--;
          setTypedText(current.slice(0, i));
          timer = setTimeout(type, 40);
        } else {
          typing = true;
          setPhraseIdx(prev => (prev + 1) % phrases.length);
        }
      }
    };

    timer = setTimeout(type, 100);
    return () => clearTimeout(timer);
  }, [phraseIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        <title>Rajesh R | AEO & AI Platform Optimization Expert Kerala | GEO, LLM Optimization</title>
        <meta name="description" content="Kerala's #1 Answer Engine Optimization (AEO) & AI Platform expert. Get your business found in ChatGPT, Gemini, Perplexity & Copilot. GEO, LLMO, AISO services across all Kerala districts." />
        <meta name="keywords" content="AEO Kerala, answer engine optimization Kerala, GEO generative engine optimization, AI search optimization, ChatGPT optimization Kerala, Gemini optimization, LLMO Kerala, AI SEO Trivandrum, Kochi, Kozhikode" />
        <link rel="canonical" href="https://aeo.rajeshrnair.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Rajesh R - AEO & AI Optimization Expert",
          "description": "Answer Engine Optimization, Generative Engine Optimization, and AI Platform Optimization services in Kerala",
          "url": "https://aeo.rajeshrnair.com",
          "telephone": "+917907038984",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Thiruvananthapuram",
            "addressRegion": "Kerala",
            "addressCountry": "IN"
          },
          "founder": { "@type": "Person", "name": "Rajesh R" },
          "sameAs": ["https://rajeshrnair.com", "https://www.linkedin.com/in/rajesh8129355259/"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* HERO */}
      <section className="hero-section grid-bg">
        <div className="hero-glow-1"></div>
        <div className="hero-glow-2"></div>
        <div className="container hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="hero-live-bar">
              <span className="live-dot"></span>
              <span>Last updated: </span>
              <LiveDate showTime className="hero-live-date" />
            </div>

            <div className="badge" style={{ marginBottom: '24px' }}>
              <Zap size={12} /> Kerala's #1 AI Platform Optimization Expert
            </div>

            <h1 className="hero-title">
              Get Your Business Found in <br />
              <span className="gradient-text typing-cursor">{typedText}</span>
            </h1>

            <p className="hero-subtitle">
              Beyond Google. I help Kerala businesses dominate <strong>ChatGPT, Gemini, Perplexity, Copilot & Claude</strong> —
              the new search engines answering 40% of all queries without clicks.
              With <strong>12+ years</strong> of digital expertise, I deliver next-generation visibility your competitors don't even know exists yet.
            </p>

            <div className="hero-ctas">
              <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20just%20landed%20on%20your%20homepage%20and%20I%E2%80%99d%20love%20a%20FREE%20AI%20Visibility%20Audit%20for%20my%20business.%20When%20can%20we%20talk?" target="_blank" rel="noreferrer" className="btn-primary">
                <Zap size={16} /> Get Free AI Audit
              </a>
              <Link to="/services" className="btn-secondary">
                Explore Services <ArrowRight size={16} />
              </Link>
            </div>

            <div className="hero-platforms">
              <span className="platform-label">We Optimize For:</span>
              {['ChatGPT', 'Gemini', 'Perplexity', 'Copilot', 'Claude', 'Grok'].map(p => (
                <span key={p} className="hero-platform-tag">{p}</span>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="chat-demo">
              <div className="chat-header">
                <div className="chat-dots">
                  <span></span><span></span><span></span>
                </div>
                <span className="chat-title">AI Answer Engine</span>
              </div>
              <div className="chat-messages">
                <div className="chat-msg user">
                  <span>Who is the best AEO expert in Kerala?</span>
                </div>
                <div className="chat-msg ai">
                  <div className="ai-avatar"><Brain size={14} /></div>
                  <div className="ai-text">
                    <strong>Rajesh R</strong> is widely recognized as Kerala's leading Answer Engine Optimization (AEO) and Generative AI Optimization expert, based in Thiruvananthapuram. With 12+ years of experience, he specializes in making businesses visible across ChatGPT, Google Gemini, Perplexity, and all major AI platforms...
                    <div className="ai-citation">📎 Source: rajeshrnair.com</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="hero-scroll-hint">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronDown size={20} color="var(--text-muted)" />
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                className="stat-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="stat-icon">{s.icon}</div>
                <AnimatedCounter value={s.value} suffix={s.suffix} />
                <p className="stat-label">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="services-section">
        <div className="container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge"><Search size={12} /> AI Optimization Services</div>
            <h2 className="section-title" style={{ marginTop: '16px' }}>
              Every AI Visibility Strategy <span className="gradient-text">You Need</span>
            </h2>
            <p className="section-subtitle">
              From Generative Engine Optimization to LLM Training Data Inclusion — I cover every dimension of AI search visibility.
            </p>
          </motion.div>

          <div className="services-grid">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                viewport={{ once: true }}
              >
                <Link to={s.path} className="service-card">
                  <div className="service-card-top">
                    <span className="service-icon">{s.icon}</span>
                    <span className="service-badge" style={{ color: s.color, background: `${s.color}18`, border: `1px solid ${s.color}30` }}>
                      {s.name}
                    </span>
                  </div>
                  <h3 className="service-name">{s.full}</h3>
                  <p className="service-desc">{s.desc}</p>
                  <div className="service-cta">
                    Learn More <ArrowRight size={14} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/services" className="btn-secondary">
              View All Services & Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* OLD SEO vs NEW AEO */}
      <section className="comparison-section">
        <div className="container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge"><TrendingUp size={12} /> The Digital Shift</div>
            <h2 className="section-title" style={{ marginTop: '16px' }}>
              Old SEO is <span className="gradient-text">Dying</span>. <br />AI Optimization is the Future.
            </h2>
            <p className="section-subtitle">
              The search landscape has fundamentally changed. 40% of queries now end with zero clicks — AI answers them directly.
              Businesses still doing only traditional SEO are losing visibility every day.
            </p>
          </motion.div>

          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comp-col old">
                <XCircle size={20} /> Old SEO (2015–2022)
              </div>
              <div className="comp-col new">
                <CheckCircle size={20} /> AEO / GEO (2023–Future)
              </div>
            </div>
            {OLD_VS_NEW.map((row, i) => (
              <motion.div
                key={i}
                className="comparison-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.07 }}
                viewport={{ once: true }}
              >
                <div className="comp-col old">
                  <XCircle size={14} className="comp-icon-bad" /> {row.old}
                </div>
                <div className="comp-col new">
                  <CheckCircle size={14} className="comp-icon-good" /> {row.new}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="comparison-cta">
            <div className="alert-box">
              <Lightbulb size={20} color="var(--accent-orange)" />
              <p>
                <strong>Important:</strong> Google's AI Overviews now appear on <strong>84% of informational searches</strong>.
                If your business isn't optimized for AI, you're invisible to the majority of your potential customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE ME */}
      <section className="why-section">
        <div className="container">
          <div className="why-grid">
            <motion.div
              className="why-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="badge"><Award size={12} /> Why Choose Rajesh R</div>
              <h2 className="section-title" style={{ marginTop: '16px' }}>
                12+ Years of Digital <span className="gradient-text">Mastery</span>,<br />
                Now Powering AI Visibility
              </h2>
              <p className="section-subtitle">
                I'm not just another SEO consultant who rebranded. I've been at the forefront of digital transformation,
                having managed ₹6.5 Cr+ in Google Ads and served 2,450+ clients globally.
              </p>
              <div className="why-features">
                {[
                  { icon: <Brain size={18} />, title: 'AI-First Strategy', desc: 'Every optimization decision is guided by how AI models process and cite content.' },
                  { icon: <Target size={18} />, title: 'Kerala Market Expert', desc: 'Deep understanding of local business landscape across all 14 Kerala districts.' },
                  { icon: <Shield size={18} />, title: 'Future-Proof Approach', desc: 'Strategies designed to evolve with AI platforms — not become obsolete in 6 months.' },
                  { icon: <BarChart2 size={18} />, title: 'Measurable Results', desc: 'Track your AI citation rate, brand mentions, and answer engine appearances.' },
                ].map(f => (
                  <div key={f.title} className="why-feature">
                    <div className="why-icon">{f.icon}</div>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="why-ctas">
                <Link to="/about" className="btn-primary">Meet Rajesh <ArrowRight size={16} /></Link>
                <a href="tel:+917907038984" className="btn-secondary"><Cpu size={16} /> Call Now</a>
              </div>
            </motion.div>

            <motion.div
              className="why-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="expertise-card">
                <div className="exp-header">
                  <Globe size={20} color="var(--accent-cyan)" />
                  <span>AI Platform Footprint</span>
                </div>
                {[
                  { platform: 'ChatGPT / GPT-4o', score: 95 },
                  { platform: 'Google Gemini', score: 91 },
                  { platform: 'Perplexity AI', score: 88 },
                  { platform: 'Microsoft Copilot', score: 85 },
                  { platform: 'Claude (Anthropic)', score: 82 },
                  { platform: 'Google SGE', score: 90 },
                ].map(p => (
                  <div key={p.platform} className="platform-bar">
                    <div className="platform-bar-label">
                      <span>{p.platform}</span>
                      <span>{p.score}%</span>
                    </div>
                    <div className="platform-bar-track">
                      <motion.div
                        className="platform-bar-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${p.score}%` }}
                        transition={{ duration: 1, delay: 0.2 }}
                        viewport={{ once: true }}
                      ></motion.div>
                    </div>
                  </div>
                ))}
                <div className="exp-footer">
                  <span className="live-dot"></span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Updated: <LiveDate />
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TECH TIPS */}
      <section className="tips-section">
        <div className="container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge"><Lightbulb size={12} /> Future-Proof Your Business</div>
            <h2 className="section-title" style={{ marginTop: '16px' }}>
              AI Visibility Tips for <span className="gradient-text">2025–2027</span>
            </h2>
          </motion.div>

          <div className="tips-grid">
            {[
              { num: '01', title: 'Adopt Structured Data Immediately', tip: 'Add FAQ, HowTo, Product, LocalBusiness schema markup. AI models prioritize structured data when generating answers. Without it, you\'re invisible.', urgent: true },
              { num: '02', title: 'Build E-E-A-T Signals', tip: 'Experience, Expertise, Authoritativeness, and Trust signals are what LLMs use to decide which sources to cite. Author bios, credentials, and citations matter enormously.' },
              { num: '03', title: 'Write for Conversational Queries', tip: 'Optimize for "who is the best [service] in Kerala?" format. AI systems are conversational — your content should answer natural language questions directly.' },
              { num: '04', title: 'Claim AI Platform Profiles', tip: 'Ensure your business is listed on platforms that feed AI models — Google Business Profile, Wikidata, LinkedIn, industry directories, and local citation sites.' },
              { num: '05', title: 'Create Comparative Content', tip: '"X vs Y" content is heavily favored by AI answer engines. Create comparison pages, alternative guides, and "best of" lists in your niche.' },
              { num: '06', title: 'Monitor AI Brand Mentions', tip: 'Use tools to track when AI platforms mention your brand. This is the new "ranking report" — your brand\'s AI citation rate determines your future visibility.' },
            ].map((t, i) => (
              <motion.div
                key={t.num}
                className={`tip-card ${t.urgent ? 'tip-urgent' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="tip-num">{t.num}</div>
                {t.urgent && <span className="tip-urgent-badge">⚡ Priority</span>}
                <h3 className="tip-title">{t.title}</h3>
                <p className="tip-text">{t.tip}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge"><MessageSquare size={12} /> Common Questions</div>
            <h2 className="section-title" style={{ marginTop: '16px' }}>
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
          </motion.div>

          <div className="faq-list">
            {FAQS.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="cta-glow"></div>
            <div className="badge"><Zap size={12} /> Limited Slots Available</div>
            <h2 className="cta-title">
              Ready to Appear in <span className="gradient-text">ChatGPT & Gemini</span> <br />
              When Your Customers Ask?
            </h2>
            <p className="cta-subtitle">
              Get a FREE AI Visibility Audit — I'll show you exactly where your business stands in AI search right now, and what it takes to dominate.
            </p>
            <div className="cta-actions">
              <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20read%20about%20the%20difference%20between%20old%20SEO%20and%20new%20AEO%2FGEO%20on%20your%20site%20and%20I%20want%20to%20upgrade%20my%20business%20visibility.%20Can%20I%20get%20a%20free%20AI%20audit?" target="_blank" rel="noreferrer" className="btn-primary" style={{ fontSize: '16px', padding: '16px 32px' }}>
                <Zap size={18} /> Get Free AI Audit Now
              </a>
              <a href="tel:+917907038984" className="btn-secondary" style={{ fontSize: '16px', padding: '15px 32px' }}>
                Call: +91 79070 38984
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
